import { generateUUID } from '@dealstack/frontend/shared/other/react/generateUUID';
import {
	createContext,
	PropsWithChildren,
	useContext,
	useEffect,
	useMemo,
	useReducer,
} from 'react';
import { AddBreadcrumbItem, BreadcrumbItem } from './useBreadcrumbs.types.ts';
import { PredefinedIcons } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.types.ts';

export const BreadcrumbsContext = createContext<Breadcrumbs | null>(null);

type BreadcrumbState = {
	breadcrumbs: BreadcrumbItem[];
	separator: SeparatorState;
};

type SeparatorState = {
	icon?: PredefinedIcons;
	skipFirst: boolean;
	isBlack?: boolean;
};
type Breadcrumbs = {
	breadcrumbs: BreadcrumbItem[];
	separator: SeparatorState;
	addBreadcrumb: (item: BreadcrumbItem) => void;
	removeBreadcrumb: (id: string) => void;
	setSeparator: (item: SeparatorState) => void;
};

export const DEFAULT_SEPARATOR_ICON: PredefinedIcons = 'backslash-alt';

export const DEFAULT_SEPARATOR: SeparatorState = {
	icon: DEFAULT_SEPARATOR_ICON,
	skipFirst: false,
};

const INITIAL_STATE: BreadcrumbState = {
	breadcrumbs: [],
	separator: DEFAULT_SEPARATOR,
};

enum ACTION {
	ADD_BREADCRUMB,
	REMOVE_BREADCRUMB,
	SET_SEPARATOR,
}

type ActionAddBreadcrumb = { type: ACTION.ADD_BREADCRUMB; payload: BreadcrumbItem };
type ActionRemoveBreadcrumb = { type: ACTION.REMOVE_BREADCRUMB; payload: string };
type ActionSetSeparator = { type: ACTION.SET_SEPARATOR; payload: SeparatorState };
type Action = ActionAddBreadcrumb | ActionRemoveBreadcrumb | ActionSetSeparator;

function reducer(state: BreadcrumbState, action: Action): BreadcrumbState {
	switch (action.type) {
		case ACTION.ADD_BREADCRUMB:
			return {
				...state,
				breadcrumbs: [...state.breadcrumbs, action.payload],
			};
		case ACTION.REMOVE_BREADCRUMB:
			return {
				...state,
				breadcrumbs: state.breadcrumbs.filter(
					(breadcrumb) => breadcrumb.id !== action.payload
				),
			};
		case ACTION.SET_SEPARATOR:
			return {
				...state,
				separator: action.payload,
			};
	}
	return state;
}

export const BreadcrumbsProvider = ({ children }: PropsWithChildren) => {
	const [state, dispatch] = useReducer(reducer, INITIAL_STATE);

	const breadcrumbAPI: Breadcrumbs = useMemo(
		() => ({
			breadcrumbs: state.breadcrumbs,
			separator: state.separator,
			addBreadcrumb: (item: BreadcrumbItem) => {
				dispatch({
					type: ACTION.ADD_BREADCRUMB,
					payload: item,
				});
			},
			removeBreadcrumb: (id: string) => {
				dispatch({
					type: ACTION.REMOVE_BREADCRUMB,
					payload: id,
				});
			},
			setSeparator: (item: SeparatorState) => {
				dispatch({
					type: ACTION.SET_SEPARATOR,
					payload: item,
				});
			},
		}),
		[state, dispatch]
	);

	return (
		<BreadcrumbsContext.Provider value={breadcrumbAPI}>{children}</BreadcrumbsContext.Provider>
	);
};

export function useBreadcrumbsState(): {
	breadcrumbs: BreadcrumbItem[];
	separator: SeparatorState;
} {
	const breadcrumbsAPI = useContext(BreadcrumbsContext);
	if (!breadcrumbsAPI) {
		throw new Error('useBreadcrumbsState should be used inside of BreadcrumbsProvider');
	}

	return { breadcrumbs: breadcrumbsAPI.breadcrumbs, separator: breadcrumbsAPI.separator };
}

/** [MIP-703] I.Czyryk 29.05.2023
 * This argument supports auto memoization without relying on the useMemo() function.
 */
export const useBreadcrumbs = (
	breadcrumbs: AddBreadcrumbItem[],
	separator: SeparatorState = INITIAL_STATE.separator
) => {
	const breadcrumbsAPI = useContext(BreadcrumbsContext);
	if (!breadcrumbsAPI) {
		throw new Error('useBreadcrumbs should be used inside of BreadcrumbsProvider');
	}

	const dependencyHash = useMemo(
		() =>
			breadcrumbs
				.flatMap((breadcrumb) => [
					breadcrumb.name,
					breadcrumb.url,
					breadcrumb.element?.props.deals?.map((deal) => deal.name)?.join(','),
				])
				.join(';'),
		[breadcrumbs]
	);

	useEffect(() => {
		breadcrumbsAPI.setSeparator(separator);
	}, [separator.skipFirst, separator.icon]);

	useEffect(() => {
		const areBreadcrumbsReady = breadcrumbs.every((breadcrumb) => breadcrumb.name);

		if (!areBreadcrumbsReady) {
			return;
		}
		const ids: string[] = [];

		for (const breadcrumb of breadcrumbs) {
			const id = generateUUID();
			ids.push(id);

			breadcrumbsAPI.addBreadcrumb({
				id,
				...breadcrumb,
			});
		}

		return () => {
			for (const id of ids) {
				breadcrumbsAPI.removeBreadcrumb(id);
			}
		};
	}, [dependencyHash]);
};
